// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import * as PollCommands from '@app/features/channel/commands/PollCommands';
import styles from '@app/features/channel/components/MessagePoll.module.css';
import PollVotes from '@app/features/channel/state/PollVotes';
import {CANCEL_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import Permission from '@app/features/permissions/state/Permission';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import Users from '@app/features/user/state/Users';
import {pushApiErrorModal} from '@app/lib/forms';
import {Permissions} from '@fluxer/constants/src/ChannelConstants';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {CheckCircleIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect} from 'react';

const END_POLL_DESCRIPTOR = msg({
	message: 'End poll',
	comment: 'Button on a poll message that closes voting early, and the title of its confirmation dialog.',
});

const POLL_ENDED_DESCRIPTOR = msg({
	message: 'Poll ended',
	comment: 'Label on a poll message whose voting is closed.',
});

const POLL_ENDS_DESCRIPTOR = msg({
	message: 'Poll ends {relativeTime}',
	comment:
		'Label on an open poll. {relativeTime} is a localized relative time such as "in 3 hours", so put it after the verb as the language needs.',
});

const TOTAL_VOTERS_DESCRIPTOR = msg({
	message: 'Total voters: {count}',
	comment: 'Label on a poll message showing how many members voted. {count} is a number.',
});

function formatRelativeEnd(i18n: I18n, expiresAt: string): string {
	const remainingMs = Math.max(0, new Date(expiresAt).getTime() - Date.now());
	const minutes = Math.max(1, Math.round(remainingMs / 60_000));
	const formatter = new Intl.RelativeTimeFormat(i18n.locale, {numeric: 'auto'});
	if (minutes < 60) return formatter.format(minutes, 'minute');
	const hours = Math.round(minutes / 60);
	if (hours < 48) return formatter.format(hours, 'hour');
	return formatter.format(Math.round(hours / 24), 'day');
}

interface MessagePollProps {
	message: Message;
	isPreview?: boolean;
}

export const MessagePoll: React.FC<MessagePollProps> = observer(({message, isPreview = false}) => {
	const {i18n} = useLingui();
	const base = message.poll;

	useEffect(() => {
		if (base && !isPreview) void PollVotes.load(message.channelId, message.id);
	}, [base, isPreview, message.channelId, message.id]);

	if (!base) return null;

	const poll = PollVotes.getEffectivePoll(message.id, base);
	const mine = PollVotes.getMine(message.id) ?? [];
	const canEnd =
		!poll.ended &&
		!isPreview &&
		(message.author.id === Users.currentUser?.id ||
			Permission.can(Permissions.MANAGE_MESSAGES, {channelId: message.channelId}));
	const interactive = !poll.ended && !isPreview;

	const handleVote = async (answerId: number) => {
		const previous = mine;
		const next = poll.allow_multiselect
			? previous.includes(answerId)
				? previous.filter((id) => id !== answerId)
				: [...previous, answerId]
			: previous.includes(answerId)
				? []
				: [answerId];
		PollVotes.setMine(message.id, next);
		try {
			PollVotes.applyStatus(message.id, base, await PollCommands.voteInPoll(message.channelId, message.id, next));
		} catch (error) {
			PollVotes.setMine(message.id, previous);
			pushApiErrorModal(i18n, error);
		}
	};

	const handleEnd = () => {
		ModalCommands.push(
			modal(() => (
				<ConfirmModal
					title={i18n._(END_POLL_DESCRIPTOR)}
					description={<Trans>Voting closes right away and nobody can change their answer.</Trans>}
					primaryText={i18n._(END_POLL_DESCRIPTOR)}
					primaryVariant="danger"
					secondaryText={i18n._(CANCEL_DESCRIPTOR)}
					onPrimary={async () => {
						try {
							PollVotes.applyStatus(message.id, base, await PollCommands.endPoll(message.channelId, message.id));
						} catch (error) {
							pushApiErrorModal(i18n, error);
						}
					}}
					data-flx="channel.message-poll.handle-end.confirm-modal"
				/>
			)),
		);
	};

	return (
		<div className={styles.poll} data-flx="channel.message-poll.poll">
			{interactive && (
				<p className={styles.hint} data-flx="channel.message-poll.hint">
					{poll.allow_multiselect ? <Trans>Select one or more answers</Trans> : <Trans>Select one answer</Trans>}
				</p>
			)}
			<div className={styles.answers} data-flx="channel.message-poll.answers">
				{poll.answers.map((answer) => {
					const selected = mine.includes(answer.id);
					const percent = poll.total_voters > 0 ? Math.round((answer.votes / poll.total_voters) * 100) : 0;
					return (
						<button
							key={answer.id}
							type="button"
							className={clsx(styles.answer, selected && styles.answerSelected)}
							disabled={!interactive}
							aria-pressed={selected}
							onClick={() => void handleVote(answer.id)}
							data-flx="channel.message-poll.answer.button"
						>
							<span className={styles.bar} style={{width: `${percent}%`}} data-flx="channel.message-poll.bar" />
							<span className={styles.answerText} data-flx="channel.message-poll.answer-text">
								{selected && (
									<CheckCircleIcon size={16} weight="fill" data-flx="channel.message-poll.check-circle-icon" />
								)}
								{answer.text}
							</span>
							<span className={styles.answerStats} data-flx="channel.message-poll.answer-stats">
								{answer.votes} · {percent}%
							</span>
						</button>
					);
				})}
			</div>
			<div className={styles.footer} data-flx="channel.message-poll.footer">
				<span data-flx="channel.message-poll.span">{i18n._(TOTAL_VOTERS_DESCRIPTOR, {count: poll.total_voters})}</span>
				<span className={styles.footerActions} data-flx="channel.message-poll.footer-actions">
					<span data-flx="channel.message-poll.span--2">
						{poll.ended
							? i18n._(POLL_ENDED_DESCRIPTOR)
							: i18n._(POLL_ENDS_DESCRIPTOR, {relativeTime: formatRelativeEnd(i18n, poll.expires_at)})}
					</span>
					{canEnd && (
						<Button
							variant="secondary"
							compact
							type="button"
							onClick={handleEnd}
							data-flx="channel.message-poll.button.end"
						>
							{i18n._(END_POLL_DESCRIPTOR)}
						</Button>
					)}
				</span>
			</div>
		</div>
	);
});
